import React, { useCallback, useMemo } from "react";
import { object } from "prop-types";
import Select from "react-select";
import "./async-select.css";
import { optionsType } from "./asyncSelectOptionTypes";
import { useDispatch, useSelector } from "react-redux";
import { getOfferIds } from "../../../actions/getOfferIdsAction";
import { selectOfferIds, selectOfferIdsLoading } from "../../../selectors";
import debounce from "lodash/debounce";
import CircularProgressSVG from "./CircularProgressSVG";

const getValue = (options, value) => {
  if (!options) {
    return "";
  }

  return options.filter((option) =>
    Array.isArray(value)
      ? value.indexOf(option.value) >= 0
      : value === option.value
  );
};

const FormikAsyncSelect = ({
  field,
  form: { setFieldValue },
  menuIsOpen,
  ...props
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector(selectOfferIdsLoading);
  const offerIds = useSelector(selectOfferIds);
  const options = formatOptions(offerIds);

  const value = getValue(
    options,
    field.value === undefined ? props.value : field.value
  );

  const handleChange = (option) => {
    if (!option) {
      return setFieldValue(field.name, null);
    }
    const newValue = Array.isArray(option.value)
      ? option.map((item) => item.value)
      : option.value;

    return setFieldValue(field.name, newValue);
  };

  const backgroundColor = (disabled, value) => {
    if (disabled) {
      return "#E9ECEF";
    } else if (value.length) {
      return "#DBFFED";
    } else {
      return "#ffffff";
    }
  };

  const customStyles = {
    control: (base, state) => ({
      ...base,
      background: backgroundColor(state.isDisabled, value),
      boxShadow: state.isFocused ? null : null,
      borderColor: state.isFocused ? "#0AD172" : "#ced4da",
      borderRadius: 4,
    }),
    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
      return {
        ...styles,
        backgroundColor: isSelected ? "#DBFFED" : "#ffffff",
        borderBottom: "1px solid #ced4da",
        "&:hover": {
          backgroundColor: "#DBFFED",
        },
      };
    },
    menu: (provided) => ({
      ...provided,
      margin: 0,
      zIndex: 2,
    }),
    menuList: (provided) => ({
      ...provided,
      padding: 0,
      zIndex: 2,
    }),
    indicatorSeparator: (provided, state) => ({
      ...provided,
      backgroundColor: state.isDisabled ? "#ced4da" : "#ced4da",
    }),
    placeholder: (provided, state) => ({
      ...provided,
      opacity: state.isDisabled ? 0.7 : 1,
    }),
  };

  const debouncedFetch = useMemo(
    () =>
      debounce((input) => {
        if (input.trim() !== "") {
          dispatch(getOfferIds(input));
        }
      }, 600),
    [getOfferIds]
  );

  const handleInputChange = useCallback(
    (newValue) => {
      debouncedFetch(newValue);
      return newValue;
    },
    [debouncedFetch]
  );
  const customComponents = {
    DropdownIndicator: () => null,
    IndicatorSeparator: () => null,
    LoadingIndicator: CircularProgressSVG,
  };
  return (
    <Select
      components={customComponents}
      menuIsOpen={menuIsOpen}
      {...props}
      theme={(theme) => ({
        ...theme,
        borderRadius: 0,
        backgroundColor: "red",
        colors: {
          ...theme.colors,
          primary25: "#FFFFFF",
          primary: "#0AD172",
          primary50: "#DBFFED",
          neutral10: "red",
        },
      })}
      styles={customStyles}
      className={"dropdown-group"}
      name={field.name}
      options={options}
      value={value}
      onInputChange={handleInputChange}
      onChange={handleChange}
      onBlur={field.onBlur}
      isLoading={isLoading}
    />
  );
};

FormikAsyncSelect.propTypes = {
  options: optionsType.isRequired,
  form: object.isRequired,
  field: object.isRequired,
};

export default FormikAsyncSelect;

function formatOptions(offerIds) {
  if (!offerIds.length) return [];

  return offerIds.map((offerId) => ({
    label: `${offerId.client}/${offerId.offerId}`,
    value: offerId.offerId,
  }));
}



// WEBPACK FOOTER //
// ./src/features/score-card/components/upload-single-batch-user-form/AsyncSelect/AsyncSelect.jsx